<template>
  <div class="sample-detail-container">
    <SampleDetailHeader @reset-focus="resetFocus" :sampleData="sampleData" @submit-add-edit-form="submitOilSample" />
    <div class="flex-row">
      <div class="flex-col small-col">
        <SampleDetailBasicInfo
          :sampleData="sampleData"
          @sample-changed="sampleChanged"
          @get-baseline-sample="getBaselineSample"
          ref="basicInfo"
          :mappings="mappings"
        />
        <br />
        <SampleDetailAdditionalInfo :sampleData="sampleData" :severity="maxSampleSeverity" :mappings="mappings" />
        <br />
        <SampleDetailBillingInfo :sampleData="sampleData" :mappings="mappings" />
      </div>
      <div class="flex-col">
        <section>
          <SampleDetailPrimaryResults
            :sampleData="sampleData"
            :showAverageResults="showAverageResults"
            :mappings="mappings"
            @toggle-average-results="toggleAverageResults"
          />
        </section>
        <SampleDetailSecondaryResults
          :sampleData="sampleData"
          :showAverageResults="showAverageResults"
          :mappings="mappings"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Constants
import Endpoint from '../../../constants/api/endpoint/endpoint.constants';
import DetailComponents from './OilSampleDetailComponents';
import SampleMixin from '../../shared/SampleMixin';
import AutoCompleteServiceDataMixin from '../../shared/AutoCompleteServiceDataMixin';
import SampleCols from '../../../constants/column-name/oil-sample.column-name.constants';
import SampleLookupCols from '../../../constants/column-name/oil-sample.lookup-column.constants';
import SampleGridCols from '../../../constants/grid/oil-sample.grid.constants';
import Mappings from './OilSampleAutoCompleteMaps';
import AjaxDataService from '../../../../services/ajax.service';

export default {
  name: 'SampleDetail',
  data() {
    return {
      addEditRoute: Endpoint.OilSample.addEditRoute,
      getEndpoint: Endpoint.OilSample.individualSample,
      baselineSampleEndpoint: Endpoint.OilSample.baselineSampleEndpoint,
      putEndpoint: Endpoint.OilSample.updateSample,
      postEndpoint: Endpoint.OilSample.addSample,
      deleteEndpoint: Endpoint.OilSample.data,
      deletePrecheckEndpoint: Endpoint.OilSample.deletePrecheck,
      newSampleEndpoint: Endpoint.OilSample.new,
      sampleData: {},
      showAverageResults: false,
      columnConstants: { ...SampleCols, ...SampleLookupCols },
      gridConstants: SampleGridCols,
      mappings: Mappings,
      idField: SampleCols.oilSampleId.field
    };
  },
  components: DetailComponents,
  mixins: [SampleMixin, AutoCompleteServiceDataMixin],
  mounted() {
    this.loadAllData();
  },
  methods: {
    submitOilSample(stayOnSample, isDelete) {
      this.setSymbolData('water');

      const decimalCols = this.gridConstants.columns.filter(item => item.roundDecimals !== undefined);
      const dataKeys = Object.keys(this.sampleData);
      const redirectURL = '../oil-samples';
      if (decimalCols !== undefined && decimalCols.length) {
        decimalCols.forEach(({ decimalPlaces, roundDecimals, field, roundOnSave }) => {
          if (roundOnSave !== undefined && !roundOnSave) {
            return;
          }

          const caseInsensitiveKey = dataKeys.find(key => key.toLowerCase() === field.toLowerCase());
          const dataVal = this.sampleData[caseInsensitiveKey];
          const roundedData = parseFloat(this.roundVal(decimalPlaces, roundDecimals, parseFloat(dataVal)));
          if (!Number.isNaN(roundedData)) {
            this.sampleData[caseInsensitiveKey] = roundedData;
          }
        });
      }
      this.onFormSubmit(stayOnSample, isDelete, redirectURL);
    },
    getBaselineSample(fluidID) {
      this.getBaselineData(fluidID);
    },
    async getBaselineData(fluidID) {
      const sampleEndpoint = Endpoint.OilSample.baselineSample;
      try {
        const { data } = await AjaxDataService.getData(sampleEndpoint, { fluidID });
        // Filter and map 'average' properties
        const averageProperties = Object.entries(data)
          .filter(([key]) => key.startsWith('average'))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
        this.sampleData = { ...this.sampleData, ...averageProperties };
        this.showAverageResults = this.showAverages();
        if (this.showAveragesResults) {
          this.setAngleBracketDisplayValue('water');
          this.setHardnessAngleBracketDisplayValue('hardness');
        }
      } catch (error) {
        console.error('There was a problem retrieving baseline data.', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
}

.flex-col {
  border-color: rgba(0, 0, 0, 0.08);
  border-style: solid;
  border-width: 0 1px;
  display: flex;
  flex: 1;
  flex-basis: 58%;
  flex-direction: column;
  padding: 10px 10px 50px 10px;

  &.small-col {
    flex-basis: 30%;
  }

  section {
    border-color: #000;
    border-style: dotted;
    border-width: 0 0 1px;
    padding: 8px;
  }
}

.sample-detail-container {
  :deep(.form-field p) {
    font-size: 100%;
    line-height: 1;
    padding: 5px 0;
  }

  :deep(.form-field *) {
    @include fontSize(16px);
  }

  :deep(.k-textbox) {
    height: 30px;
  }

  :deep(.k-textbox-label) {
    min-width: 130px;
    padding: 0 10px;
    width: 130px;
    text-align: right;
  }

  :deep(.k-checkbox-label) {
    padding-left: 27px;
  }

  :deep(.flex-group) {
    align-items: center;
    display: flex;
  }

  :deep(.flex-autocomplete-field) {
    flex-grow: 1;
  }

  :deep(.form-group) {
    margin: 3px;
  }

  :deep(.multiple-texts) {
    justify-content: space-between;
  }

  :deep(.form-checkbox) {
    flex-grow: 1;
    width: auto;
  }

  :deep(.multiselect__input),
  .multiselect__single {
    min-height: 10px;
    line-height: 10px;
  }
  :deep(.multiselect__tags) {
    min-height: 31px;
    margin-right: 0;
    padding: 5px 20px 0 8px;
  }
  :deep(.multiselect) {
    margin-top: 1px;
    min-height: 20px;
    margin-right: 5px;
  }
  :deep(.multiselect__single) {
    margin-bottom: 0;
    font-size: 14px;
  }
  :deep(.multiselect__select) {
    height: 29px;
    margin-right: 0;
    padding: 0;
    width: 25px;
  }
  :deep(.multiselect__option) {
    padding: 8px;
    min-height: 20px;
    line-height: 10px;
  }
  :deep(.multiselect__input) {
    margin-bottom: 5px;
    @include fontSize(16px);
  }
  :deep(.multiselect__placeholder) {
    margin-bottom: 0;
    padding-top: 1px;
  }
  :deep(.top-align) {
    align-items: flex-start;
  }
}
</style>
